import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// Variants alinhadas aos botoes do vanilla (.btn-primary, .btn-secondary,
// .btn-ghost, .btn-danger). Mantem semantica visual que o usuario ja conhece.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-on-primary shadow-soft hover:bg-primary-hover',
        secondary:
          'bg-surface text-text-strong border border-border-strong hover:bg-surface-mute',
        ghost: 'text-text-muted hover:bg-surface-mute hover:text-text-strong',
        danger: 'bg-danger text-white shadow-soft hover:opacity-90',
        outline: 'border border-border text-text hover:bg-surface-mute',
        link: 'text-primary underline-offset-4 hover:underline',
        dark: 'bg-[var(--color-cta-dark)] text-[var(--color-on-cta-dark)] shadow-soft hover:bg-[var(--color-cta-dark-hover)]',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4',
        lg: 'h-11 px-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
